import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import MediaLibrary from './MediaLibrary';

const { LuPlus, LuTrash2, LuMegaphone, LuCode, LuImage, LuAlignLeft, LuEdit, LuX, LuSave, LuAlignCenter, LuAlignRight, LuType, LuLayoutTemplate, LuImages } = LuIcons;

const BUCKET_NAME = 'images';

const AD_TYPES = [
  { id: 'text', label: 'نص إعلاني', icon: LuAlignLeft },
  { id: 'image', label: 'صورة (أو سلايدر)', icon: LuImage },
  { id: 'code', label: 'كود HTML', icon: LuCode },
];

const AD_LOCATIONS = [
  { id: 'top_bar', label: 'الشريط العلوي (نص أو صورة واحدة فقط)' },
  { id: 'home_above_cars', label: 'الرئيسية: فوق السيارات' },
  { id: 'home_bottom', label: 'الرئيسية: أسفل الموقع' },
  { id: 'calc_above', label: 'الحاسبة: فوقها' },
  { id: 'calc_below', label: 'الحاسبة: تحتها' },
];

const FONT_SIZES = [
  { val: 'sm', label: 'صغير (14px)' },
  { val: 'base', label: 'متوسط (16px)' },
  { val: 'lg', label: 'كبير (18px)' },
  { val: 'xl', label: 'كبير جداً (20px)' },
  { val: '2xl', label: 'ضخم (24px)' },
];

const TEXT_ALIGNS = [
  { val: 'right', label: 'يمين', icon: LuAlignRight },
  { val: 'center', label: 'وسط', icon: LuAlignCenter },
  { val: 'left', label: 'يسار', icon: LuAlignLeft },
];

const initialAdForm = {
  title: '',
  type: 'image',
  location: 'home_above_cars',
  content: '',
  images: [],
  target_url: '',
  font_size: 'base',
  text_align: 'right',
  is_marquee: false,
  marquee_direction: 'rtl',
  marquee_speed: 15,
  sort_order: 0
};

const AdForm = ({ initial, onSave, onCancel, title }) => {
  const [formData, setFormData] = useState(initial || initialAdForm);
  const [uploadProgress, setUploadProgress] = useState('');
  const [showLibrary, setShowLibrary] = useState(false);

  // استبعاد الشريط العلوي إذا كان نوع الإعلان كود
  const availableLocations = formData.type === 'code' 
    ? AD_LOCATIONS.filter(l => l.id !== 'top_bar') 
    : AD_LOCATIONS;

  useEffect(() => {
    if (formData.type === 'code' && formData.location === 'top_bar') {
      setFormData(prev => ({ ...prev, location: 'home_above_cars' }));
    }
  }, [formData.type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadProgress('جاري الحفظ...');
    try {
      let finalContent = formData.content;
      let finalImages = formData.images || [];

      // إذا كان الإعلان نوع صورة، نعتمد على مصفوفة الصور
      if (formData.type === 'image') {
        if (finalImages.length === 0 && !finalContent) {
          alert('يرجى اختيار صورة واحدة على الأقل أو وضع رابط مباشر.');
          setUploadProgress('');
          return;
        }
        // نضع أول صورة في الحقل القديم للتوافق
        if (finalImages.length > 0) {
          finalContent = finalImages[0];
        }
      }

      await onSave({ ...formData, content: finalContent, images: finalImages });
    } catch (err) {
      alert(`حدث خطأ: ${err.message}`);
    } finally {
      setUploadProgress('');
    }
  };

  const removeImage = (idx) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx)
    }));
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="mb-8 bg-slate-50 p-5 rounded-2xl border border-slate-200" dir="rtl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-slate-800">{title}</h3>
          <button type="button" onClick={onCancel} className="p-1.5 text-slate-400 hover:text-slate-600 bg-slate-200 rounded-lg">
            <SafeIcon icon={LuX} className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">عنوان الإعلان (اختياري)</label>
            <input 
              type="text" 
              value={formData.title} 
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none font-bold"
              placeholder="مثال: شاهد جديد السيارات"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">مكان الظهور</label>
            <select 
              value={formData.location} 
              onChange={e => setFormData({...formData, location: e.target.value})}
              className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
            >
              {availableLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.label}</option>)}
            </select>
          </div>
          <div>
             <label className="block text-sm font-medium text-slate-700 mb-1">ترتيب العرض (الأقل يظهر أولاً)</label>
             <input 
               type="number" 
               value={formData.sort_order || 0} 
               onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
               className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
               placeholder="0, 1, 2..."
             />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">نوع الإعلان</label>
          <div className="flex gap-3 flex-wrap sm:flex-nowrap">
            {AD_TYPES.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData({...formData, type: type.id, content: ''})}
                className={`flex-1 min-w-[100px] flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-colors ${formData.type === type.id ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <SafeIcon icon={type.icon} className="w-6 h-6 mb-1" />
                <span className="text-xs font-bold">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Text Ad Settings */}
        {formData.type === 'text' && (
          <div className="space-y-4 mb-4 bg-white p-4 rounded-xl border border-slate-200">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">محتوى النص الإعلاني</label>
              <textarea 
                required 
                value={formData.content} 
                onChange={e => setFormData({...formData, content: e.target.value})}
                rows="3" 
                dir="rtl"
                className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">حجم الخط</label>
                <select 
                  value={formData.font_size} 
                  onChange={e => setFormData({...formData, font_size: e.target.value})}
                  className="w-full p-2.5 border border-slate-200 rounded-lg bg-white outline-none"
                >
                  {FONT_SIZES.map(fs => <option key={fs.val} value={fs.val}>{fs.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">محاذاة النص</label>
                <select 
                  value={formData.text_align} 
                  onChange={e => setFormData({...formData, text_align: e.target.value})}
                  disabled={formData.is_marquee}
                  className="w-full p-2.5 border border-slate-200 rounded-lg bg-white outline-none disabled:bg-slate-100"
                >
                  {TEXT_ALIGNS.map(ta => <option key={ta.val} value={ta.val}>{ta.label}</option>)}
                </select>
              </div>
            </div>

            {/* Marquee Options */}
            <div className="pt-3 border-t border-slate-100">
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input 
                  type="checkbox" 
                  checked={formData.is_marquee || false} 
                  onChange={e => setFormData({...formData, is_marquee: e.target.checked})}
                  className="w-5 h-5 accent-indigo-600"
                />
                <span className="font-bold text-slate-700">جعل الإعلان متحركاً (Marquee)</span>
              </label>

              {formData.is_marquee && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">اتجاه الحركة</label>
                    <select 
                      value={formData.marquee_direction || 'rtl'} 
                      onChange={e => setFormData({...formData, marquee_direction: e.target.value})}
                      className="w-full p-2.5 border border-slate-200 rounded-lg bg-white outline-none"
                    >
                      <option value="rtl">من اليمين إلى اليسار (عربي)</option>
                      <option value="ltr">من اليسار إلى اليمين</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">سرعة الحركة (ثواني - أقل يعني أسرع)</label>
                    <input 
                      type="number" 
                      min="1" max="100"
                      value={formData.marquee_speed || 15} 
                      onChange={e => setFormData({...formData, marquee_speed: parseInt(e.target.value) || 15})}
                      className="w-full p-2.5 border border-slate-200 rounded-lg bg-white outline-none"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Image / Slider Settings */}
        {formData.type === 'image' && (
          <div className="space-y-4 mb-4 bg-white p-4 rounded-xl border border-slate-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-2">
              <label className="block text-sm font-bold text-slate-700">صور الإعلان (يمكنك إضافة أكثر من صورة لعمل سلايدر)</label>
              <button 
                type="button" 
                onClick={() => setShowLibrary(true)}
                className="text-sm bg-indigo-600 text-white font-bold px-4 py-2 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
              >
                <SafeIcon icon={LuImages} className="w-4 h-4" /> اختيار من المكتبة
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-2">إذا اخترت أكثر من صورة، سيتم عرضها بشكل شرائح تتحرك تلقائياً.</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
              {(formData.images || []).map((url, idx) => (
                <div key={idx} className="relative group aspect-[16/9] rounded-xl border-2 border-slate-200 overflow-hidden bg-slate-50 shadow-sm">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button 
                    type="button" 
                    onClick={() => removeImage(idx)}
                    className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <SafeIcon icon={LuTrash2} className="w-6 h-6 text-white mb-1" />
                    <span className="text-white text-[10px] font-bold">إزالة</span>
                  </button>
                </div>
              ))}

              <button 
                type="button" 
                onClick={() => setShowLibrary(true)}
                className="aspect-[16/9] rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
              >
                <SafeIcon icon={LuPlus} className="w-6 h-6 mb-1" />
                <span className="text-xs font-bold">إضافة صورة</span>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">رابط التوجيه عند النقر (اختياري)</label>
              <input 
                type="url" 
                value={formData.target_url} 
                onChange={e => setFormData({...formData, target_url: e.target.value})}
                dir="ltr"
                className="w-full p-2.5 border border-slate-200 rounded-lg outline-none text-left"
                placeholder="https://..."
              />
            </div>
          </div>
        )}

        {/* Code Settings */}
        {formData.type === 'code' && (
          <div className="mb-4 bg-white p-4 rounded-xl border border-slate-200">
            <label className="block text-sm font-medium text-slate-700 mb-1">ألصق كود AdSense أو HTML</label>
            <textarea 
              required 
              value={formData.content} 
              onChange={e => setFormData({...formData, content: e.target.value})}
              rows="4" 
              dir="ltr"
              className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
            />
          </div>
        )}

        <div className="flex gap-3 pt-2 border-t border-slate-200">
          <button 
            type="submit" 
            disabled={!!uploadProgress}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-60"
          >
            <SafeIcon icon={LuSave} className="w-4 h-4" /> {uploadProgress || 'حفظ الإعلان'}
          </button>
          <button 
            type="button" 
            onClick={onCancel}
            className="bg-slate-200 hover:bg-slate-300 text-slate-800 px-6 py-2.5 rounded-lg font-medium transition-colors"
          >
            إلغاء
          </button>
        </div>
      </form>

      {/* Media Library Modal */}
      {showLibrary && (
        <MediaLibrary 
          onClose={() => setShowLibrary(false)} 
          onSelect={(selectedUrls) => {
            setFormData(prev => ({
              ...prev,
              images: [...(prev.images || []), ...selectedUrls]
            }));
            setShowLibrary(false);
          }}
        />
      )}
    </>
  );
};

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingAd, setEditingAd] = useState(null);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('advertisements').select('*').order('created_at', { ascending: false });
    if (!error) setAds(data || []);
    setLoading(false);
  };

  const handleAdd = async (formData) => {
    const { error } = await supabase.from('advertisements').insert([{
      title: formData.title,
      type: formData.type,
      location: formData.location,
      content: formData.content,
      images: formData.images || [],
      target_url: formData.target_url,
      font_size: formData.font_size || 'base',
      text_align: formData.text_align || 'right',
      is_marquee: formData.is_marquee || false,
      marquee_direction: formData.marquee_direction || 'rtl',
      marquee_speed: formData.marquee_speed || 15,
      sort_order: formData.sort_order || 0,
      is_active: true
    }]);
    if (!error) {
      setIsAdding(false);
      fetchAds();
    }
  };

  const handleEdit = async (formData) => {
    if (!editingAd) return;
    const { error } = await supabase.from('advertisements').update({
      title: formData.title,
      type: formData.type,
      location: formData.location,
      content: formData.content,
      images: formData.images || [],
      target_url: formData.target_url,
      font_size: formData.font_size || 'base',
      text_align: formData.text_align || 'right',
      is_marquee: formData.is_marquee || false,
      marquee_direction: formData.marquee_direction || 'rtl',
      marquee_speed: formData.marquee_speed || 15,
      sort_order: formData.sort_order || 0
    }).eq('id', editingAd.id);
    if (!error) {
      setEditingAd(null);
      fetchAds();
    }
  };

  const toggleStatus = async (ad) => {
    await supabase.from('advertisements').update({ is_active: !ad.is_active }).eq('id', ad.id);
    fetchAds();
  };

  const handleDelete = async (id) => {
    if (window.confirm('حذف هذا الإعلان؟')) {
      await supabase.from('advertisements').delete().eq('id', id);
      fetchAds();
    }
  };

  const renderPreview = (ad) => {
    if (ad.type === 'image') {
      const imgUrl = ad.images && ad.images.length > 0 ? ad.images[0] : ad.content;
      return <img src={imgUrl} className="h-12 w-16 rounded object-cover" />;
    }
    if (ad.type === 'code') return <div className="text-xs bg-slate-100 p-2 rounded h-12 flex items-center">&lt;Code/&gt;</div>;
    return <div className="text-sm text-slate-700 line-clamp-2 max-w-[120px]">{ad.content}</div>;
  };

  // عرض الإعلانات مرتبة حسب المكان والترتيب
  const sortedAds = [...ads].sort((a, b) => {
    if (a.location !== b.location) return a.location.localeCompare(b.location);
    return (a.sort_order || 0) - (b.sort_order || 0);
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" dir="rtl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-800 flex items-center"><SafeIcon icon={LuMegaphone} className="w-6 h-6 me-2 text-indigo-600" /> إدارة الإعلانات</h2>
        {!isAdding && !editingAd && (
          <button onClick={() => setIsAdding(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2"><SafeIcon icon={LuPlus} className="w-5 h-5" /> إضافة إعلان</button>
        )}
      </div>

      {isAdding && <AdForm title="إضافة إعلان جديد" onSave={handleAdd} onCancel={() => setIsAdding(false)} />}
      {editingAd && <AdForm title={`تعديل الإعلان`} initial={editingAd} onSave={handleEdit} onCancel={() => setEditingAd(null)} />}

      {loading ? (
        <div className="text-center py-10 text-slate-500">جاري التحميل...</div>
      ) : (
        <div className="space-y-3">
          {sortedAds.map(ad => (
            <div key={ad.id} className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl border gap-4 ${ad.is_active ? 'border-indigo-200 bg-indigo-50/30' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex gap-3 items-center flex-1 overflow-hidden w-full">
                <div className="flex-shrink-0">{renderPreview(ad)}</div>
                <div className="min-w-0">
                  <h3 className={`font-bold truncate ${ad.is_active ? 'text-indigo-900' : 'text-slate-500'}`}>{ad.title || 'بدون عنوان'}</h3>
                  <div className="flex gap-1.5 text-xs mt-1 flex-wrap">
                    <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">{AD_TYPES.find(t => t.id === ad.type)?.label || ad.type}</span>
                    <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">{AD_LOCATIONS.find(l => l.id === ad.location)?.label || ad.location}</span>
                    <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-bold">ترتيب: {ad.sort_order || 0}</span>
                    {ad.type === 'image' && ad.images && ad.images.length > 1 && (
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">سلايدر ({ad.images.length})</span>
                    )}
                    {ad.is_marquee && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">متحرك ({ad.marquee_speed || 15}s)</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-end">
                <button onClick={() => { setEditingAd(ad); setIsAdding(false); }} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"><SafeIcon icon={LuEdit} className="w-4 h-4" /></button>
                <button onClick={() => toggleStatus(ad)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${ad.is_active ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>{ad.is_active ? 'إخفاء' : 'تفعيل'}</button>
                <button onClick={() => handleDelete(ad.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><SafeIcon icon={LuTrash2} className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {ads.length === 0 && !isAdding && <p className="text-center text-slate-500 py-6">لا توجد إعلانات.</p>}
        </div>
      )}
    </div>
  );
};

export default AdminAds;